import { Box, Popover, Stack } from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import React, { ChangeEvent, useRef, useState } from "react"
import {
  Heading,
  SegmentedToggle,
  ViraButton,
  ViraCheckbox,
  ViraLink,
  ViraText,
  resolveViraPalette,
} from "@ksana/vira-ui"
import { TranscriptSessionModality } from "../../utils/transcriptApi"

interface TranscriptConsentPopoverProps {
  anchorEl: HTMLElement | null
  isConsentChecked: boolean
  isCheckingAccess: boolean
  errorMessage: string | null
  isBusy: boolean
  isFileUploading?: boolean
  onConsentCheckedChange: (isChecked: boolean) => void
  onClose: () => void
  onConfirm: (modality: TranscriptSessionModality) => void
  onUploadFile?: (file: File, modality: TranscriptSessionModality) => void
  onCreateEmptySession?: (modality: TranscriptSessionModality) => void
}

/**
 * Collects session modality and recording consent before starting a transcript
 * session, uploading a transcript file, or creating a plan-only session shell.
 */
export default function TranscriptConsentPopover({
  anchorEl,
  isConsentChecked,
  isCheckingAccess,
  errorMessage,
  isBusy,
  isFileUploading = false,
  onConsentCheckedChange,
  onClose,
  onConfirm,
  onUploadFile,
  onCreateEmptySession,
}: TranscriptConsentPopoverProps) {
  const hasError = Boolean(errorMessage)
  const [sessionType, setSessionType] =
    useState<TranscriptSessionModality>("in_person")
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const uploadBusy = isFileUploading
  const actionDisabled =
    isCheckingAccess || hasError || !isConsentChecked || isBusy || uploadBusy

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (file && onUploadFile && !actionDisabled) {
      onUploadFile(file, sessionType)
    }
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: (theme) => {
            const vira = resolveViraPalette(theme)
            return {
              borderRadius: "12px",
              border: `1px solid ${vira.border}`,
              bgcolor: "background.paper",
              color: vira.ink,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 10px 24px rgba(0, 0, 0, 0.45)"
                  : "0px 10px 14px rgba(30, 38, 60, 0.16)",
              maxWidth: 360,
              mt: 1,
            }
          },
        },
      }}
    >
      <Stack spacing={2} sx={{ p: 2.5 }}>
        <Heading level="section">Start session</Heading>
        <ViraText tone="muted" sx={{ fontSize: 13.5, lineHeight: 1.45 }}>
          Record live audio, upload a transcript file, or open a session for
          action plan and progress note only. Confirm verbal consent when
          recording.
        </ViraText>
        <SegmentedToggle
          aria-label="Session type"
          value={sessionType}
          onChange={setSessionType}
          options={[
            { value: "in_person", label: "In Person" },
            { value: "telehealth", label: "Telehealth" },
          ]}
        />
        <Box
          component="label"
          sx={(theme) => {
            const vira = resolveViraPalette(theme)
            return {
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              cursor: hasError || isBusy || uploadBusy ? "default" : "pointer",
              border: `1px solid ${vira.lavenderBorder}`,
              borderRadius: "10px",
              backgroundColor: vira.lavender,
              p: 1.5,
              userSelect: "none",
            }
          }}
        >
          <ViraCheckbox
            checked={isConsentChecked}
            onChange={(event) => onConsentCheckedChange(event.target.checked)}
            disabled={hasError || isBusy || uploadBusy}
            inputProps={{
              "aria-label":
                "I confirmed recording consent verbally before starting.",
            }}
            sx={{ p: 0, mt: 0.25 }}
          />
          <ViraText
            tone="ink"
            sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4 }}
          >
            I confirmed recording consent verbally before starting.
          </ViraText>
        </Box>
        {isCheckingAccess ? (
          <ViraText tone="muted" sx={{ fontSize: 13 }}>
            Checking transcript service access...
          </ViraText>
        ) : null}
        {errorMessage ? (
          <ViraText tone="danger" sx={{ fontSize: 13 }}>
            Error: {errorMessage}
          </ViraText>
        ) : null}
        <Stack spacing={1}>
          <ViraButton
            variant="secondary"
            showAccentDot
            onClick={() => onConfirm(sessionType)}
            disabled={actionDisabled}
            fullWidth
          >
            Start recording
          </ViraButton>
          {onUploadFile ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                hidden
                onChange={handleFileSelected}
              />
              <ViraButton
                variant="secondary"
                fullWidth
                disabled={actionDisabled}
                onClick={() => fileInputRef.current?.click()}
                startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
              >
                {uploadBusy ? "Uploading…" : "Upload transcript file"}
              </ViraButton>
            </>
          ) : null}
        </Stack>
        {onCreateEmptySession ? (
          <ViraText tone="muted" sx={{ fontSize: 12.5, lineHeight: 1.45 }}>
            No recording?{" "}
            <ViraLink
              onClick={() => {
                const planOnlyDisabled =
                  isCheckingAccess || hasError || isBusy || uploadBusy
                if (!planOnlyDisabled) {
                  onCreateEmptySession(sessionType)
                }
              }}
              sx={{
                pointerEvents:
                  isCheckingAccess || hasError || isBusy || uploadBusy
                    ? "none"
                    : undefined,
                opacity:
                  isCheckingAccess || hasError || isBusy || uploadBusy
                    ? 0.5
                    : 1,
              }}
            >
              Create session
            </ViraLink>
          </ViraText>
        ) : null}
      </Stack>
    </Popover>
  )
}
